import React, { useState, useEffect } from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// WeeklyMasterProvider と同じ型
export type Suggestion = { id: number; label: string };

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface TaskInputProps {
  suggestions: Suggestion[];
  placeholder?: string;
  onChange?: (values: Suggestion[]) => void;
  initialValue?: Suggestion[];
}

const MultiSelectWithCheckboxes: React.FC<TaskInputProps> = ({
  suggestions,
  placeholder,
  onChange,
  initialValue = [],
}) => {
  // Autocomplete の "value"（選択済みチップの元）
  const [values, setValues] = useState<Suggestion[]>(initialValue);

  // 親から渡された initialValue が変わったら同期する
  useEffect(() => {
    setValues(initialValue);
  }, [initialValue]);

  // 値が変わったとき（チェックON/OFF）
  const handleChange = (_: React.SyntheticEvent, newVal: Suggestion[]) => {
    setValues(newVal);
    onChange?.(newVal);
  };

  // ▼ option の1行（ドロップダウンの行）
  //   MUIが渡してくれる props の中に key が入っているので、
  //   key を抜き出して <li key={key} ...> の形にする
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Suggestion,
    state: AutocompleteRenderOptionState
  ) => {
    const { key, ...liProps } = props as {
      key?: React.Key;
    } & React.HTMLAttributes<HTMLLIElement>;

    return (
      <li key={key} {...liProps}>
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          sx={{ mr: 1 }}
          checked={state.selected}
        />
        {option.label}
      </li>
    );
  };

  return (
    <Autocomplete<Suggestion, true, false, false>
      multiple
      disableCloseOnSelect
      options={suggestions}
      value={values}
      onChange={handleChange}
      renderOption={renderOption}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      // Chip の描画。getTagProps() も key を含んでいるので同じように key を分離する
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const chipProps = getTagProps({ index });

          const { key, ...restChipProps } = chipProps as {
            key?: React.Key;
          } & React.ComponentProps<typeof Chip>;

          return (
            <Chip
              key={key}
              {...restChipProps}
              deleteIcon={<CloseIcon fontSize="small" />}
              label={option.label}
              sx={(theme) => ({
                borderRadius: 4,
                backgroundColor: theme.palette.mode === 'dark' ? '#334155' : '#4169E1',
                color: '#fff',
                fontSize: '0.95rem',
                '& .MuiChip-label': {
                  lineHeight: '30px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                },
                '& .MuiChip-deleteIcon': {
                  color: '#fff',
                  marginLeft: '4px',
                  marginRight: '4px',
                  fontSize: '1rem',
                  '&:hover': { color: '#eee' },
                },
                height: '32px',
                margin: '2px',
              })}
            />
          );
        })
      }
      sx={{ width: '100%', marginTop: 0, paddingTop: 0 }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={values.length ? '' : placeholder}
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              height: 40,
              display: 'flex',
              alignItems: 'center',
              paddingTop: 0,
              paddingBottom: 0,
              boxSizing: 'border-box',
            },
            '& .MuiOutlinedInput-root': {
              width: '100%',
              height: '100%',
              backgroundColor: 'var(--input-bg, #fff)',
              borderRadius: 'var(--border-radius, 4px)',
              padding: '0 var(--autocomplete-padding-x, 8px)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--chip-gap, 4px)',
              alignItems: 'center',
              boxSizing: 'border-box',
              '& fieldset': {
                borderColor: 'var(--border-color-lighter, #e0e0e0)',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: 'var(--border-color-light-hover, #bdbdbd)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--input-focus-border, #2563eb)',
                borderWidth: '1px',
                boxShadow: '0 0 0 2px rgba(37,99,235,.2)',
              },
            },
            '& .MuiInputBase-input, & .MuiAutocomplete-input': {
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              paddingLeft: 'var(--input-inner-padding-x, 4px) !important',
              paddingRight: 'var(--input-inner-padding-x, 4px) !important',
              fontSize: 'var(--base-font-size, 0.9rem)',
              color: 'var(--text-primary-color, var(--text-color, #e2e8f0))',
              border: 'none !important',
              outline: 'none !important',
              boxShadow: 'none !important',
              backgroundColor: 'transparent !important',
              flexGrow: 1,
              minWidth: '60px',
              width: 'auto',
              height: 'auto',
            },
            '& .MuiInputBase-input::placeholder': {
              opacity: 1,
            },
          }}
        />
      )}
    />
  );
};

export default MultiSelectWithCheckboxes;